import type { ComponentChildren, RefObject } from 'preact'
import { useRef, useState } from 'preact/hooks'
import { LoaderCircle, Maximize, Minimize, Pause, Play, Volume2, VolumeX } from 'lucide-preact'
import { playedFraction, shownLengthSeconds } from '../player/positions'
import type { ReplayResume } from '../player/positions'
import { RoomPlayer } from '../player/RoomPlayer'
import { streamOnAir } from '../player/source'
import { useHasPlayed, useLiveGeneration, usePausedPrompt } from '../player/usePlayer'
import type { Player } from '../player/usePlayer'
import { useSeekSlider } from '../player/useSeekSlider'
import { useRoomSession } from '../session/roomSession'
import { RoomSubtitleControls } from '../subtitles/SubtitleControls'
import { formatRecordingClock, formatSessionClock } from '../time'
import type { WebinarRoomSubtitlesDto } from '../types'

/**
 * The stage while there is video, and the room's own controls for it (Mux Player's are hidden, see RoomPlayer).
 * Nothing of ours sits over the bottom 19.6% of the picture: the client's title band is baked into the recording, so
 * the live controls sit top right and the replay's bar sits under the picture.
 */

const FAILURE_CLASS = 'v-fail'

interface StageProps {
    player: Player
    /** The client's brand colour, for the player's own spinner and dialog. */
    accentColor: string | null
    title: string
}

/** A line over the stage, clear of the client's title band. */
function StageHold({ children }: { children: ComponentChildren }) {
    return <div class="v-hold" role="status"><span class="pill">{children}</span></div>
}

/**
 * The room's own Play over a paused player. It goes the moment the video starts, so a keyboard user's focus on it would
 * drop to the top of the page: `focusAfterPlay` is where it goes instead, a control that stays on screen.
 */
export function PlayPrompt({ player, focusAfterPlay }: { player: Player; focusAfterPlay?: RefObject<HTMLElement> }) {
    if (!usePausedPrompt(player)) return null
    const play = (event: MouseEvent) => {
        const hadFocus = document.activeElement === event.currentTarget
        player.play()
        if (hadFocus) focusAfterPlay?.current?.focus()
    }
    return (
        <button type="button" class="v-play" aria-label="Play" onClick={play}>
            <Play size={26} class="ic" fill="currentColor" stroke="none" aria-hidden="true" />
        </button>
    )
}

/**
 * The live stream, from the moment Mux has it on the air: a player mounted before then is refused and gives up
 * (`streamOnAir`), and the stream coming back after it ended gets a fresh one. Offline, the page cannot know the
 * stream's state, so its baked stream plays for the whole session.
 */
export function ViewerLiveStage({ player, accentColor, title, offline }: StageProps & { offline: boolean }) {
    const { store } = useRoomSession()
    const { room, playback } = store.state.value
    const generation = useLiveGeneration(room.VideoState)
    const playbackId = playback.LivePlaybackId
    const token = playback.LiveToken

    let body: ComponentChildren
    if (!playbackId || !token) {
        body = <StageHold>The video can’t play on this page. Reload to try again.</StageHold>
    } else if (!offline && !streamOnAir(room.VideoState)) {
        body = <StageHold><LoaderCircle size={17} class="ic spin" aria-hidden="true" />Starting, stay on this page.</StageHold>
    } else {
        body = (
            <>
                <RoomPlayer
                    key={generation}
                    player={player}
                    source={{ kind: 'live', playbackId, playbackToken: token, accentColor, title }}
                    failureClass={FAILURE_CLASS}
                />
                <PlayPrompt player={player} />
            </>
        )
    }
    return <div class="v-feed">{body}</div>
}

/**
 * The replay of the original recording, on demand, from where `resume` says; its bar is `ViewerReplayBar`, whose
 * play/pause is `playToggle`. It waits for the viewer's Play: a replay that started by itself counted a tab left open on
 * the page as on-demand viewing. The centre Play is only that first one; after it, a pause is the viewer's, often to
 * read a slide, and the bar plays it again without a disc over the picture.
 */
export function ViewerReplayStage({ player, accentColor, title, resume, playToggle }: StageProps & {
    resume: ReplayResume
    playToggle: RefObject<HTMLButtonElement>
}) {
    const { store } = useRoomSession()
    const started = useHasPlayed(player)
    const { ReplayPlaybackId: playbackId, ReplayToken: token, ThumbnailToken: thumbnailToken } = store.state.value.playback
    if (!playbackId || !token) return <div class="v-feed" />
    return (
        <div class="v-feed">
            <RoomPlayer
                player={player}
                source={{ kind: 'on-demand', playbackId, playbackToken: token, thumbnailToken, startSeconds: resume.startSeconds, autoplay: false, accentColor, title }}
                failureClass={FAILURE_CLASS}
            />
            {!started && <PlayPrompt player={player} focusAfterPlay={playToggle} />}
        </div>
    )
}

interface ControlProps {
    player: Player
    /** What full screen fills, so the room's controls stay on screen. */
    fullscreenTarget: RefObject<HTMLElement>
    buttonClass?: string
    /** The recording's subtitles, which the page draws itself: CC is offered only with them. */
    subtitles?: WebinarRoomSubtitlesDto | null
}

function FullscreenButton({ player, fullscreenTarget, buttonClass }: ControlProps) {
    const { fullscreen } = player.media.value
    return (
        <button
            type="button"
            class={buttonClass}
            aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
            onClick={() => player.toggleFullscreen(fullscreenTarget.current)}
        >
            {fullscreen ? <Minimize size={18} class="ic" aria-hidden="true" /> : <Maximize size={18} class="ic" aria-hidden="true" />}
        </button>
    )
}

function PauseButton({ player }: { player: Player }) {
    const { paused } = player.media.value
    return (
        <button type="button" aria-label={paused ? 'Play the video' : 'Pause the video'} onClick={() => player.togglePlay()}>
            {paused
                ? <Play size={18} class="ic" fill="currentColor" stroke="none" aria-hidden="true" />
                : <Pause size={18} class="ic" fill="currentColor" stroke="none" aria-hidden="true" />}
        </button>
    )
}

/**
 * The live stream's controls, top right: sound, subtitles when the recording has them, and full screen. There is no
 * timeline. A browser that only lets the stream start muted leaves it muted, so while it is, the controls stay in
 * view and say Unmute in words. The waiting video is `pausable` as well (WCAG 2.2.2); nothing pauses the session.
 */
export function ViewerLiveControls({ player, fullscreenTarget, subtitles, pausable = false }: ControlProps & { pausable?: boolean }) {
    const media = player.media.value
    if (media.status !== 'ready') return null
    return (
        <div class={`vctrls${media.muted ? ' is-muted' : ''}`}>
            {pausable && <PauseButton player={player} />}
            {media.muted
                ? <button type="button" class="v-unmute" onClick={() => player.toggleMute()}><VolumeX size={18} class="ic" aria-hidden="true" />Unmute</button>
                : <button type="button" aria-label="Mute" onClick={() => player.toggleMute()}><Volume2 size={18} class="ic" aria-hidden="true" /></button>}
            <RoomSubtitleControls subtitles={subtitles} menu="below" />
            <FullscreenButton player={player} fullscreenTarget={fullscreenTarget} />
        </div>
    )
}

interface ReplayBarProps extends ControlProps {
    resume: ReplayResume
    /**
     * The recording's length from the room: what the prepared file measured, which every screen shows. Mux's copy reads a
     * few hundredths longer, so the player's own reading stands in only where the room has none.
     */
    recordingDurationSeconds: number | null | undefined
    /** Its play/pause, where the stage's first Play hands focus. */
    playToggle: RefObject<HTMLButtonElement>
}

/**
 * The replay's bar under the picture: the seek bar, play and pause, the time, where it picked up
 * with "Watch from the start", then sound, subtitles and full screen.
 */
export function ViewerReplayBar({ player, resume, recordingDurationSeconds, fullscreenTarget, playToggle, subtitles }: ReplayBarProps) {
    const media = player.media.value
    const length = shownLengthSeconds(recordingDurationSeconds, player.duration.value)
    const slider = useSeekSlider({ label: 'Seek', timeSeconds: player.time.value, durationSeconds: length, onSeek: seconds => player.seek(seconds) })
    const [fromStart, setFromStart] = useState(false)
    const seekBar = useRef<HTMLDivElement>(null)
    const fraction = playedFraction(slider.shownSeconds, length)
    const pickedUp = resume.pickedUpAtSeconds
    const note = fromStart
        ? 'Playing from the start.'
        : pickedUp !== null ? `Picked up at ${formatSessionClock(pickedUp)}, where you left the live session.` : null

    const watchFromStart = () => {
        player.seek(0)
        player.play()
        setFromStart(true)
        seekBar.current?.focus()
    }

    return (
        <div class="rbar">
            <div class="rprog" ref={seekBar} {...slider.props}>
                <span class="fill" style={{ transform: `scaleX(${fraction})` }} />
                <span class="knob" style={{ left: `${fraction * 100}%` }} />
            </div>
            <div class="rrow">
                <button type="button" class="ib" ref={playToggle} aria-label={media.paused ? 'Play' : 'Pause'} onClick={() => player.togglePlay()}>
                    {media.paused
                        ? <Play size={18} class="ic" fill="currentColor" stroke="none" aria-hidden="true" />
                        : <Pause size={18} class="ic" fill="currentColor" stroke="none" aria-hidden="true" />}
                </button>
                <span class="rtime tnum">{formatSessionClock(slider.shownSeconds)} / {formatRecordingClock(length)}</span>
                {note && <span class="rnote">{note}</span>}
                {!fromStart && pickedUp !== null && <button type="button" class="rlink" onClick={watchFromStart}>Watch from the start</button>}
                <span class="rspacer" />
                <button type="button" class="ib" aria-label={media.muted ? 'Unmute' : 'Mute'} onClick={() => player.toggleMute()}>
                    {media.muted ? <VolumeX size={18} class="ic" aria-hidden="true" /> : <Volume2 size={18} class="ic" aria-hidden="true" />}
                </button>
                <RoomSubtitleControls subtitles={subtitles} buttonClass="ib" menu="above" />
                <FullscreenButton player={player} fullscreenTarget={fullscreenTarget} buttonClass="ib" />
            </div>
        </div>
    )
}
